/**
 * @file Onglet "FX" de l'ecran Skins : auras cosmetiques (effet constant autour du chat), des plus simples aux plus folles. Boutique uniquement.
 */
import type { AuraDef } from '@config/types';
import { Decimal } from '@shared/bignum';
import type { WalletId } from '@shared/enums';
import { Component } from '../../core/Component';
import { uiIcon } from '../../core/uiIcons';
import { h, setText, toggleClass } from '../../core/dom';
import { confirmAsk, send } from '../../core/feedback';
import { auraPreview } from '../../core/icons';
import { balanceOf, walletChips } from '../../core/widgets';
import type { UiContext } from '../../UiContext';

interface CardView {
  aura: AuraDef | null;
  root: HTMLElement;
  state: HTMLElement;
  costs: HTMLElement | null;
}

export class AuraPane extends Component {
  private readonly cards: CardView[] = [];

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'aura-pane');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    const grid = h('div', { class: 'aura-grid' });
    // Carte "Aucune" : retire l'aura.
    const noneState = h('div', { class: 'skin-state' });
    const none = h(
      'button',
      { class: 'skin-card aura-card', type: 'button', 'data-ui-block': true, 'data-aura': 'none', onclick: () => void send(this.ctx, { type: 'aura.equip', id: null }) },
      h('div', { class: 'aura-view' }, h('span', { class: 'aura-none' }, uiIcon('none'))),
      h('div', { class: 'skin-name' }, t.t('fx.none')),
      noneState,
    );
    this.cards.push({ aura: null, root: none, state: noneState, costs: null });
    grid.append(none);
    for (const aura of this.ctx.config.auras) {
      const state = h('div', { class: 'skin-state' });
      const costs = h('div', { class: 'skin-costs' }, ...walletChips(this.ctx, aura.cost));
      const root = h(
        'button',
        { class: 'skin-card aura-card', type: 'button', 'data-ui-block': true, 'data-aura': aura.id, onclick: () => void this.activate(aura) },
        h('div', { class: 'aura-view' }, auraPreview(aura.style)),
        h('div', { class: 'skin-name' }, t.name(aura.name)),
        state,
        costs,
      );
      this.cards.push({ aura, root, state, costs });
      grid.append(root);
    }
    this.el.append(h('p', { class: 'hint' }, t.t('fx.hint')), grid);
    this.update(0);
  }

  private async activate(aura: AuraDef): Promise<void> {
    const { data } = this.ctx.sim;
    if (data.auras.owned.includes(aura.id)) {
      void send(this.ctx, { type: 'aura.equip', id: aura.id });
      return;
    }
    if (aura.cost.dia !== undefined && !(await confirmAsk('skins.confirmGems'))) return;
    const r = send(this.ctx, { type: 'aura.buy', id: aura.id });
    if (r.ok) void send(this.ctx, { type: 'aura.equip', id: aura.id });
  }

  override update(_dt: number): void {
    const { data } = this.ctx.sim;
    const { i18n } = this.ctx;
    for (const c of this.cards) {
      const owned = c.aura === null || data.auras.owned.includes(c.aura.id);
      const equipped = c.aura === null ? data.auras.equipped === null : data.auras.equipped === c.aura.id;
      toggleClass(c.root, 'owned', owned);
      toggleClass(c.root, 'equipped', equipped);
      if (c.costs) toggleClass(c.costs, 'is-hidden', owned);
      setText(c.state, equipped ? i18n.t('common.equipped') : owned ? i18n.t('common.equip') : '');
      if (c.aura) {
        const cost = c.aura.cost;
        const affordable = (Object.keys(cost) as WalletId[]).every((id) => {
          const v = cost[id];
          return v === undefined || balanceOf(this.ctx, id).gte(Decimal.fromValue(v));
        });
        toggleClass(c.root, 'disabled', !owned && !affordable);
      }
    }
  }
}
